import { useEffect, useRef } from "react";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useTmaSocialLinking } from "@/hooks/socials/useTmaSocialLinking";
import { useHubUser } from "@/hooks/user/useHubUser";

/**
 * Hook to automatically trigger TMA social linking once after successful authentication
 * This hook should be used at the app level to ensure TMA users get their Telegram
 * account linked automatically without manual intervention
 */
export const useAutoTmaSocialLinking = () => {
  const { hubUser, authenticated, ready, identityProvider } = useHubUser();
  const { autoLinkTmaSocial, isLinking } = useTmaSocialLinking();
  const { isTMA, initDataRaw } = useTelegramContext();

  // Track if we've already attempted linking for this user session
  const hasAttemptedLinking = useRef(false);
  const lastLinkedUserId = useRef<number | null>(null);

  useEffect(() => {
    // Reset linking attempt flag when user changes
    if (hubUser?.id !== lastLinkedUserId.current) {
      hasAttemptedLinking.current = false;
      lastLinkedUserId.current = hubUser?.id || null;
    }
  }, [hubUser?.id]);

  useEffect(() => {
    // Only proceed if all conditions are met
    if (
      !authenticated ||
      !ready ||
      !hubUser ||
      !isTMA ||
      identityProvider !== "ton" ||
      !initDataRaw ||
      hasAttemptedLinking.current ||
      isLinking
    ) {
      return;
    }

    // Check if user already has a nativeTelegram social account
    const hasNativeTelegram = hubUser.socialAccounts?.some((social) => social.socialName === "nativeTelegram");

    if (hasNativeTelegram) {
      // User already has TMA social account, mark as attempted
      hasAttemptedLinking.current = true;
      return;
    }

    // Trigger auto-linking
    hasAttemptedLinking.current = true;
    autoLinkTmaSocial();
  }, [authenticated, ready, hubUser, isTMA, identityProvider, initDataRaw, isLinking, autoLinkTmaSocial]);
};
